// src/components/common/BadgeModal.jsx
import React, { useEffect, useRef } from "react";
import { BADGES } from "../../utils/Badges";

const BadgeModal = ({ totalAssessments, onClose }) => {
  const dialogRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement;
    const node = dialogRef.current;
    if (!node) return;

    const focusable = node.querySelectorAll(
      'button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])'
    );
    focusable[0]?.focus();

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === "Tab") {
        const nodes = Array.from(focusable).filter(
          (el) => !el.disabled && el.offsetParent !== null
        );
        if (!nodes.length) return;

        const first = nodes[0];
        const last = nodes[nodes.length - 1];

        if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      }
    };

    node.addEventListener("keydown", handleKeyDown);

    return () => {
      if (node) node.removeEventListener("keydown", handleKeyDown);
      previouslyFocusedRef.current?.focus();
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      role="presentation"
      aria-hidden="true"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="badge-modal-title"
        className="bg-white rounded-lg p-6 w-11/12 max-w-sm space-y-4 outline-none"
      >
        <h3 id="badge-modal-title" className="text-xl font-bold text-center">
          Your Badges
        </h3>

        <ul className="grid grid-cols-2 gap-4" role="list">
          {BADGES.map((b) => {
            const unlocked = totalAssessments >= b.threshold;
            return (
              <li
                key={b.name}
                role="listitem"
                aria-label={`${b.name} badge ${unlocked ? "unlocked" : "locked"}`}
              >
                <div
                  className={`flex flex-col items-center p-3 border rounded-lg transition ${
                    unlocked ? "border-green-500" : "border-gray-300 opacity-40"
                  }`}
                >
                  <span
                    className="text-3xl"
                    style={{ color: b.color }}
                    aria-hidden="true"
                  >
                    {typeof b.icon === "function" ? b.icon({ size: 24 }) : b.icon}
                  </span>
                  <p className="text-sm font-semibold">{b.name}</p>
                  <p className="text-[10px] text-gray-500">
                    {isFinite(b.threshold)
                      ? `${b.threshold} assessments`
                      : "MAX level"}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>

        <button
          onClick={onClose}
          className="block mt-4 px-4 py-2 mx-auto bg-purple-600 text-white rounded"
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default BadgeModal;